import React, { useState } from "react"

import ParentalComponent from "./ParentalComponent"
import ReactSelectionElement from "../../../../shared_components/widgets/ReactSelectionElement"
import { withAppSettingsConfig } from "../../../../config/AppSettingsConfigProvider"
import { withFermManagementConfig } from "../../../../config/FermManagementConfigProvider"
import InfoPopover from "../../../../shared_components/widgets/InfoPopover"
import { getTimestamp, getCurrentTimestamp } from "./utils"

function InoculationParents({
  intl,
  //FermManagementConfigProvider
  bioCatStocks,
  canInoculateSeparately,
  liveRuns = [],
  externalSamples,
  setExternalSamples,
  externalRuns,
  setExternalRuns,
  // --- FermManagementConfigProvider
  canEditParentalInfo = false,
  canUseSKBCParents = false,
  run_name = "",
  started_on = "",
  inoculated_on = "",
  runs = [],
  stocks = [],
  external_samples = [],
  external_runs = [],
  updateField,
  BIOCONTROL_NAME,
}) {
  const liveFermRuns = liveRuns.filter((r) => !r.placeholder)
  const potentialStock = bioCatStocks.filter((stock) => stock.available_quantity > 0)
  const availableStock = potentialStock.filter((s) => !stocks.map((_) => _.instance.id).includes(s.id))
  const stockSelectionDisabled = availableStock.length === 0 || !canEditParentalInfo
  const availableRuns = liveFermRuns.filter(
    (r) => !runs.map((_) => _.instance.name).includes(r.name) && r.name !== run_name,
  )
  const runSelectionDisabled = availableRuns.length === 0 || !canEditParentalInfo

  const parentProps = {
    updateField: updateField,
    showButton: canEditParentalInfo,
    canRemoveRow: canEditParentalInfo,
    intl: intl,
  }
  const hasBCParents = [...external_samples, ...external_runs].length > 0
  const hasLTPMParents = [...stocks, ...runs].length > 0

  const currentTimestamp = getCurrentTimestamp()
  const [inoWarn, setInoWarn] = useState(false)
  const inoDateMsg = intl.formatMessage({
    id: "fermmanagement.inoculated.after.started",
    defaultMessage:
      "'Inoculated on' date cannot be set earlier in time than the 'started on' date or " +
      "later in time than the current date",
  })

  return (
    <div style={{ padding: "10px", backgroundColor: "var(--lanzalake-blue-bright)" }}>
      {started_on && inoculated_on && (
        <div className={"row"} style={{ marginBottom: "15px" }}>
          <div className={"col-6"}>
            <label style={{ margin: "0" }}>
              {intl.formatMessage({ id: "fermmanagement.started.on", defaultMessage: "Started on" })}:
            </label>
            {/*
              Showing started on date for user reference ONLY. User CANNOT alter it currently as there is a relationship
              between the start date of one run and the ending date of a previous placeholder run. To keep things
              easier in terms of run data assignment we do not allow for the alteration of a run start date
            */}
            <input
              type={"datetime-local"}
              className={"form-control form-control-sm text-center"}
              value={started_on}
              step={1}
              disabled={true}
            />
          </div>
          <div className={"col-6"}>
            <div style={{ display: "flex" }}>
              <label style={{ margin: "0" }}>
                {intl.formatMessage({ id: "fermmanagement.inoculated.on", defaultMessage: "Inoculated on" })}:
              </label>
              {updateField && canInoculateSeparately && (
                <div style={{ marginLeft: "auto" }}>
                  <InfoPopover desc={inoDateMsg} placement={"bottom"} />
                </div>
              )}
            </div>
            {inoWarn && (
              <div style={{ fontStyle: "italic", fontSize: "85%", color: "var(--lanzalake-grey-mid)" }}>
                {inoDateMsg}
              </div>
            )}
            <input
              type={"datetime-local"}
              className={"form-control form-control-sm text-center"}
              value={inoculated_on}
              step={1}
              disabled={!updateField || !canInoculateSeparately}
              onChange={(e) => {
                let ts = getTimestamp(e.target.value)
                if (ts && updateField) {
                  if (ts < started_on) {
                    // Cannot set the inoculation date before the current set 'started on' date
                    setInoWarn(true)
                    ts = started_on
                  } else if (ts > currentTimestamp) {
                    // Cannot set the inoculate date to a date in the future
                    setInoWarn(true)
                    ts = currentTimestamp
                  } else {
                    setInoWarn(false)
                  }
                  updateField("inoculationDate", null, ts, null, "field")
                }
              }}
            />
          </div>
        </div>
      )}
      <div>
        <div>
          {canUseSKBCParents && (canEditParentalInfo || hasLTPMParents) && (
            <u>
              <label>
                <b>LTPM</b>:
              </label>
            </u>
          )}
          {/* Biocatalyst Select Button & Table */}
          <ParentalComponent
            {...parentProps}
            field={"stocks"}
            selectedOpts={stocks}
            getMatchingInstance={(stock) => bioCatStocks.find((s) => stock.instance.id === s.id)}
            headers={[
              {
                id: "navigation.bioCatalystOption",
                default: "BioCatalyst",
                ColDisplay: (instance, _, idx) => {
                  const getObj = (s) => ({
                    ...s,
                    value: `${s.sku.name} (${intl.formatMessage({
                      id: "stock.lot",
                    })}: ${s.lot_no})`,
                  })
                  return (
                    <ReactSelectionElement
                      disabled={stockSelectionDisabled}
                      list={availableStock.map((s) => getObj(s))}
                      onValueChange={(_, opt) => updateField("stocks", "instance", opt, idx, "listField")}
                      value={instance ? getObj(instance) : null}
                      isMulti={false}
                      isClearable={false}
                      divStyle={{ marginBottom: "0", fontSize: "15px" }}
                    />
                  )
                },
              },
              {
                id: "stock.quantity",
                default: "Quantity",
                ColDisplay: (instance, entry, idx) => (
                  <input
                    type={"number"}
                    min={"1"}
                    max={`${instance?.available_quantity}` || "1"}
                    disabled={!instance?.id || !canEditParentalInfo}
                    className={"form-control text-center"}
                    value={entry?.count}
                    onChange={(e) => {
                      e.preventDefault()
                      const availableQuant = instance?.available_quantity
                      let value = Math.round(e.target.value)
                      if (value !== "" && value > availableQuant) {
                        value = availableQuant
                      } else if (value !== "" && value < 1) {
                        value = 1
                      }
                      updateField("stocks", "count", value, idx, "listField")
                    }}
                  />
                ),
              },
              {
                id: "stock.uom",
                default: "UoM",
                ColDisplay: (instance, _, __) => <span>{instance?.uom || "-"}</span>,
              },
            ]}
            disableAddRowButton={stockSelectionDisabled || potentialStock.length === stocks.length}
          />
          {/* Run Select Table & Button */}
          <ParentalComponent
            {...parentProps}
            field={"runs"}
            selectedOpts={runs}
            getMatchingInstance={(run) => liveFermRuns.find((r) => run.instance.name === r.name)}
            headers={[
              {
                id: "general.run",
                default: "Run",
                ColDisplay: (instance, entry, idx) => {
                  const r = instance?.name ? instance : entry?.instance?.name ? entry.instance : null // If reference run is no longer 'live' we won't have an instance match
                  const getObj = (o) => ({
                    ...o,
                    value: !o?.name ? "" : !o?.reactor ? o.name : `${o.name} (${o?.reactor})`,
                  })
                  return (
                    <ReactSelectionElement
                      disabled={runSelectionDisabled}
                      list={availableRuns.map((r) => getObj(r))}
                      onValueChange={(_, opt) => updateField("runs", "instance", opt, idx, "listField")}
                      value={r ? getObj(r) : null}
                      isMulti={false}
                      isClearable={false}
                      divStyle={{ marginBottom: "0", fontSize: "15px" }}
                    />
                  )
                },
              },
            ]}
            disableAddRowButton={runSelectionDisabled || liveFermRuns.length === runs.length}
          />
        </div>
        {canUseSKBCParents && (
          <div>
            {(canEditParentalInfo || (hasLTPMParents && hasBCParents)) && <hr />}
            {(canEditParentalInfo || hasBCParents) && (
              <u>
                <label>
                  <b>{BIOCONTROL_NAME}</b>:
                </label>
              </u>
            )}
            {/* External BioControl Samples Table, Button, & Search */}
            <ParentalComponent
              {...parentProps}
              field={"external_samples"}
              selectedOpts={external_samples}
              headers={[
                {
                  id: "general.external.sample",
                  defaultMessage: "External Sample",
                  ColDisplay: (_, es, idx) => {
                    const getObj = (o) => ({
                      ...o,
                      value: `${intl.formatMessage({ id: "general.id" })}: ${
                        o.name || o.id
                      }, ${intl.formatMessage({
                        id: "general.description",
                      })}: ${o.description}`,
                    })
                    return (
                      <ReactSelectionElement
                        disabled={externalSamples.length === 0 || !canEditParentalInfo}
                        list={externalSamples
                          .filter((s) => !external_samples.map((_) => _.instance.id).includes(s.id))
                          .map((s) => getObj(s))}
                        onValueChange={(_, opt) =>
                          updateField("external_samples", "instance", opt, idx, "listField")
                        }
                        value={es?.instance?.id ? getObj(es.instance) : null}
                        isMulti={false}
                        isClearable={false}
                        divStyle={{ marginBottom: "0", fontSize: "15px" }}
                      />
                    )
                  },
                },
              ]}
              disableAddRowButton={!canEditParentalInfo}
              removeRow={() => external_samples.length === 1 && setExternalSamples([])}
              infoPopoverMsg={"Results are limited to samples taken within the last year"}
            />
            {/* External BioControl Runs Table, Button, & Search */}
            <ParentalComponent
              {...parentProps}
              field={"external_runs"}
              selectedOpts={external_runs}
              headers={[
                {
                  id: "general.external.run",
                  defaultMessage: "External Run",
                  ColDisplay: (_, er, idx) => {
                    const getObj = (o) => ({
                      ...o,
                      value: `${intl.formatMessage({ id: "general.id" })}: ${
                        o.name || o.id
                      }, ${intl.formatMessage({
                        id: "general.description",
                      })}: ${o.description}`,
                    })
                    return (
                      <ReactSelectionElement
                        disabled={externalRuns.length === 0 || !canEditParentalInfo}
                        list={externalRuns
                          .filter((r) => !external_runs.map((_) => _.instance.id).includes(r.id))
                          .map((r) => getObj(r))}
                        onValueChange={(_, opt) =>
                          updateField("external_runs", "instance", opt, idx, "listField")
                        }
                        value={er?.instance?.id ? getObj(er.instance) : null}
                        isMulti={false}
                        isClearable={false}
                        divStyle={{ marginBottom: "0", fontSize: "15px" }}
                      />
                    )
                  },
                },
              ]}
              disableAddRowButton={!canEditParentalInfo}
              removeRow={() => external_runs.length === 1 && setExternalRuns([])}
              infoPopoverMsg={"Results are limited to runs created within the last year"}
            />
          </div>
        )}
      </div>
    </div>
  )
}

export default withAppSettingsConfig(withFermManagementConfig(InoculationParents))
